import React, { useState, useContext, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, Row, Col, Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faThumbtack, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import AuthContext from 'context/auth-context'
import customColumnService from '../../services/customColumnService'
import { useDeletePinnedField } from '../../api/hooks/use-dashboard-pinned-fields'
import { twoDigitMoney, noDigitMoney } from 'helpers/utils'
import { formatDateSlash } from 'helpers/datetime'
import { formatSecondsAsDays } from 'shared/column-filters/timeInQueueUnit'
import MutedText from 'shared/forms/muted-text'

const PinnedFieldsCard = ({ pinnedFields, transaction, dashboardId, canEdit = true }) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const [editingValues, setEditingValues] = useState({})
  const isInitializedRef = useRef(false)
  const valuesLoadedRef = useRef(false)

  // Check if there are any pinned custom columns
  const hasPinnedCustomColumns = useMemo(() => {
    return pinnedFields.some((pf) => pf.pinnable_type === 'CustomColumn')
  }, [pinnedFields])

  // Fetch custom column values for this transaction (only if there are pinned custom columns)
  const { data: customColumnValuesData = {}, isLoading: valuesLoading } =
    useQuery(
      ['customColumnValues', dashboardId, transaction.id],
      () =>
        customColumnService.getCustomColumnValues(token, dashboardId, [
          transaction.id
        ]),
      {
        enabled: !!dashboardId && !!token && !!transaction.id && hasPinnedCustomColumns
      }
    )

  // Get values for this transaction - memoized to prevent unnecessary re-renders
  const transactionValues = useMemo(() => {
    return customColumnValuesData[transaction.id] || {}
  }, [customColumnValuesData, transaction.id])

  // Mutation for deleting pinned fields
  const deletePinnedFieldMutation = useDeletePinnedField({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(['dashboard-pinned-fields', dashboardId])
      }
    }
  })

  const handleRemovePinnedField = (pinnedFieldId) => {
    deletePinnedFieldMutation.mutate({
      dashboardId,
      pinnedFieldId
    })
  }

  // Get the value from the transaction based on the field type and identifier
  const getFieldValue = (pinnedField) => {
    const field = pinnedField.pinnable

    if (pinnedField.pinnable_type === 'TableColumn') {
      const columnName = field.database_column_name

      // For basic vendor_id fields, return external_id instead of UUID
      if (columnName.endsWith('_vendor_id') && transaction.vendor) {
        return transaction.vendor.external_id
      }

      // Check if it's in the grouped transaction details
      if (transaction.grouped_transaction_details) {
        for (const [, fields] of Object.entries(
          transaction.grouped_transaction_details
        )) {
          const matchingField = fields.find(
            (f) => f.database_column_name === columnName
          )
          if (matchingField) {
            return matchingField.value
          }
        }
      }

      // Fallback to direct transaction property access
      return transaction[columnName]
    } else if (pinnedField.pinnable_type === 'CustomColumn') {
      // For custom columns, get from fetched custom_column_values
      const valueData = transactionValues[field.id]
      return valueData?.value || null
    }
  }

  // Mark values as loaded when we get actual data
  useEffect(() => {
    if (Object.keys(customColumnValuesData).length > 0) {
      valuesLoadedRef.current = true
    }
  }, [customColumnValuesData])

  // Reset values loaded flag when transaction changes
  useEffect(() => {
    valuesLoadedRef.current = false
  }, [transaction.id])

  // Initialize editing values for custom columns
  useEffect(() => {
    // Wait until values are ready (either loaded or query completed)
    const valuesReady = valuesLoadedRef.current || !valuesLoading

    if (pinnedFields.length > 0 && valuesReady && !isInitializedRef.current) {
      const initialValues = {}
      pinnedFields.forEach((pinnedField) => {
        if (pinnedField.pinnable_type === 'CustomColumn') {
          const valueData = transactionValues[pinnedField.pinnable.id]
          initialValues[pinnedField.pinnable.id] = valueData?.value || ''
        }
      })
      setEditingValues(initialValues)
      isInitializedRef.current = true
    }
  }, [pinnedFields, transactionValues, valuesLoading])

  // Reset initialization flag when transaction changes
  useEffect(() => {
    isInitializedRef.current = false
  }, [transaction.id])

  // Sync editingValues when transactionValues change (e.g., from CustomColumnsTab updates)
  useEffect(() => {
    if (isInitializedRef.current && Object.keys(transactionValues).length > 0) {
      setEditingValues((prev) => {
        const updated = { ...prev }
        pinnedFields.forEach((pinnedField) => {
          if (pinnedField.pinnable_type === 'CustomColumn') {
            const valueData = transactionValues[pinnedField.pinnable.id]
            if (valueData !== undefined) {
              updated[pinnedField.pinnable.id] = valueData?.value || ''
            }
          }
        })
        return updated
      })
    }
  }, [transactionValues, pinnedFields])

  // Mutation for saving custom column values
  const saveMutation = useMutation(
    (data) =>
      customColumnService.upsertCustomColumnValue(token, dashboardId, data),
    {
      onSuccess: () => {
        // Invalidate all custom column value queries to keep pinned and tab values in sync
        queryClient.invalidateQueries(['customColumnValues', dashboardId])
        queryClient.invalidateQueries(['customColumnValues', dashboardId, transaction.id])
        queryClient.invalidateQueries(['transactions'])
      },
      onError: (error) => {
        console.error('Error saving custom column value:', error)
      }
    }
  )

  const handleValueChange = (columnId, value) => {
    setEditingValues((prev) => ({
      ...prev,
      [columnId]: value
    }))
  }

  const handleSave = async (columnId) => {
    await saveMutation.mutateAsync({
      custom_column_id: columnId,
      transaction_id: transaction.id,
      value: editingValues[columnId]
    })
  }

  const formatValue = (field, value) => {
    const { display_as } = field

    // Handle null or undefined values
    if (value === null || value === undefined) {
      return '-'
    }

    // Format based on display_as type
    switch (display_as) {
      case 'money':
        return noDigitMoney(value)
      case 'two_digit_money':
        return twoDigitMoney(value)
      case 'date':
        return formatDateSlash(value)
      case 'percent':
        return `${value}%`
      case 'boolean':
        return value ? 'Yes' : 'No'
      case 'time_in_queue':
        return formatSecondsAsDays(value) || value
      default:
        return value
    }
  }

  const formatCustomColumnReadOnlyValue = (column, value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">-</span>
    }

    switch (column.column_type) {
      case 'boolean':
        return value === 'true' || value === true ? 'Yes' : 'No'
      case 'date':
        // Format date for display
        if (value) {
          const date = new Date(value)
          return date.toLocaleDateString()
        }
        return '-'
      default:
        return value
    }
  }

  const renderEditControl = (column) => {
    const value = editingValues[column.id] || ''

    // Render read-only display when user cannot edit
    if (!canEdit) {
      return <div>{formatCustomColumnReadOnlyValue(column, value)}</div>
    }

    const commonProps = {
      value,
      onChange: (e) => handleValueChange(column.id, e.target.value),
      onBlur: () => handleSave(column.id),
      size: 'sm'
    }

    switch (column.column_type) {
      case 'text':
        return (
          <Form.Control
            {...commonProps}
            as="textarea"
            rows={2}
            placeholder="Enter text..."
          />
        )

      case 'number':
        return (
          <Form.Control
            {...commonProps}
            type="number"
            placeholder="Enter number..."
          />
        )

      case 'date':
        return (
          <Form.Control
            value={value}
            onChange={(e) => {
              handleValueChange(column.id, e.target.value)
              // Auto-save for date - immediately save when date is selected
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transaction.id,
                value: e.target.value
              })
            }}
            size="sm"
            type="date"
            placeholder="Select date..."
          />
        )

      case 'boolean':
        return (
          <Form.Check
            type="checkbox"
            checked={value === 'true' || value === true}
            onChange={(e) => {
              const newValue = e.target.checked ? 'true' : 'false'
              handleValueChange(column.id, newValue)
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transaction.id,
                value: newValue
              })
            }}
          />
        )

      case 'select':
        const selectOptions = column.settings?.options || []
        return (
          <Form.Select
            {...commonProps}
            onChange={(e) => {
              handleValueChange(column.id, e.target.value)
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transaction.id,
                value: e.target.value
              })
            }}
          >
            <option value="">Select...</option>
            {selectOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        )

      case 'status':
        const statusOptions = column.settings?.options || [
          'To-do',
          'In progress',
          'Complete'
        ]
        return (
          <Form.Select
            {...commonProps}
            onChange={(e) => {
              handleValueChange(column.id, e.target.value)
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transaction.id,
                value: e.target.value
              })
            }}
          >
            <option value="">Select status...</option>
            {statusOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        )

      default:
        return (
          <Form.Control
            {...commonProps}
            type="text"
            placeholder="Enter value..."
          />
        )
    }
  }

  const isEmpty = !pinnedFields || pinnedFields.length === 0

  return (
    <Card className="mb-3">
      <Card.Body>
        {isEmpty ? (
          <div className="text-center text-muted py-3">
            <FontAwesomeIcon
              icon={faThumbtack}
              className="mb-2"
              size="2x"
              style={{ opacity: 0.3 }}
            />
            <p className="mb-0">No fields pinned yet</p>
            <small>Click any field below to pin it here</small>
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon
                icon={faThumbtack}
                className="text-primary me-2"
              />
              <h6 className="mb-0">Pinned Fields</h6>
            </div>
            <Row>
              {pinnedFields.map((pinnedField) => {
                const field = pinnedField.pinnable
                const isCustomColumn =
                  pinnedField.pinnable_type === 'CustomColumn'
                const value = getFieldValue(pinnedField)
                const displayAs = isCustomColumn
                  ? field.column_type
                  : field.display_as

                return (
                  <Col
                    key={pinnedField.id}
                    xs={12}
                    sm={6}
                    md={6}
                    lg={3}
                    className="mb-3"
                  >
                    <div className="pinned-field-item">
                      <div className="pinned-field-title d-flex align-items-center mb-1">
                        <MutedText as="div" className="small">
                          {field.display_name || field.name}
                        </MutedText>
                        {canEdit && (
                          <span
                            className="remove-icon ms-1 text-danger"
                            title="Remove pinned field"
                            style={{ fontSize: '0.75rem', cursor: 'pointer' }}
                            onClick={() => handleRemovePinnedField(pinnedField.id)}
                          >
                            <FontAwesomeIcon icon={faTimesCircle} />
                          </span>
                        )}
                      </div>
                      <div className="pinned-field-value">
                        {isCustomColumn ? (
                          renderEditControl(field)
                        ) : (
                          <div>
                            {formatValue({ display_as: displayAs }, value)}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )
              })}
            </Row>
            <style jsx>{`
              .pinned-field-value :global(.form-control),
              .pinned-field-value :global(.form-select),
              .pinned-field-value :global(.form-check) {
                margin-bottom: 0;
              }

              .pinned-field-title .remove-icon {
                opacity: 0;
                transition: opacity 0.2s ease;
              }

              .pinned-field-title:hover .remove-icon {
                opacity: 1;
              }
            `}</style>
          </>
        )}
      </Card.Body>
    </Card>
  )
}

PinnedFieldsCard.propTypes = {
  pinnedFields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired,
      pinnable_type: PropTypes.string.isRequired,
      pinnable: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired
    })
  ),
  transaction: PropTypes.object.isRequired,
  dashboardId: PropTypes.string,
  canEdit: PropTypes.bool
}

export default PinnedFieldsCard
